import { Controller } from "@hotwired/stimulus"

// CONEK-642 / CONEK-2276: Branch matrix bulk controller.
// Manages row selection, the action bar, the decoupled operation selector
// (assign / set_status / unassign / copy_branch), the source-branch selector
// for copy_branch, and the "select all N matching the filter" affordance.
export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "actionBar",
    "count",
    "idsContainer",
    "bulkAction",
    "statusSelect",
    "statusInheritOption", // the empty "keep / inherit" <option>
    "statusField", // wrapper shown only for assign/set_status
    "statusLabel", // "Status" / "Default status" depending on operation
    "statusHint", // explains that assign keeps existing statuses
    "sourceField", // wrapper shown only for copy_branch
    "promotionField", // wrapper shown only for set_promotion (CONEK-5419)
    "branchesLabel", // "2. On which branches?" / "...Copy to..." for copy_branch
    "selectAllMatching", // hidden input toggled by the "select all matching" link
    "selectAllMatchingToggle", // the visible link/button
    "pageSelectionNote", // "N on this page selected"
    "matchingNote", // "All M matching selected"
    "branchCheckbox", // CONEK-5005: each branch checkbox in step 2
    "branchSelectAllButton", // CONEK-5005: the Select All/Unselect All toggle
  ]
  static values = {
    branches: Array,
    totalMatching: Number, // total rows matching the current filter
    // i18n labels passed from ERB so no user-visible string is hardcoded in JS.
    statusLabel: { type: String, default: "Status" },
    defaultStatusLabel: { type: String, default: "Default status" },
    branchesLabel: { type: String, default: "2. On which branches?" },
    branchesLabelCopy: { type: String, default: "2. Copy to which branches?" },
    // CONEK-5005: branch select-all toggle labels, i18n-supplied from the ERB.
    selectAllBranchesLabel: { type: String, default: "Select All branches" },
    unselectAllBranchesLabel: {
      type: String,
      default: "Unselect All branches",
    },
  }

  connect() {
    this.matchingMode = false
    // Tracks whether the user explicitly chose a status in `assign` mode. Until
    // they do, assign keeps the inherit (blank) default.
    this.userPickedStatus = false
    // Previous operation, so syncOperationFields can detect *entering* assign
    // (and reset the inherit default) vs. staying within it (preserve the pick).
    this.lastAction = null
    this.#captureStatusOptions()
    this.syncOperationFields()
    this.updateUI()
    this.updateBranchToggleLabel()
  }

  // Fired when the user changes the status dropdown. A non-blank pick in assign
  // mode means "use this as the default for newly-assigned branches". Picking
  // the blank inherit option again sets the flag back to false on purpose — the
  // user deliberately chose "keep current", so a later operation switch should
  // treat assign as un-customised again.
  statusChanged() {
    if (this.hasStatusSelectTarget) {
      this.userPickedStatus = this.#statusValue() !== ""
    }
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => (cb.checked = checked))
    // Toggling page checkboxes drops out of "all matching" mode.
    this.matchingMode = false
    this.updateUI()
  }

  toggleOne() {
    const total = this.checkboxTargets.length
    const checked = this.checkboxTargets.filter((cb) => cb.checked).length

    this.selectAllTarget.checked = checked === total
    this.selectAllTarget.indeterminate = checked > 0 && checked < total
    this.matchingMode = false
    this.updateUI()
  }

  // "Select all M matching the filter" — operate on the whole filtered scope
  // server-side rather than just the visible page.
  selectAllMatching(event) {
    event.preventDefault()
    this.matchingMode = true
    // L1: visually convey "everything matching is selected" — check + disable
    // the page boxes so they can't be toggled into a confusing partial state.
    this.checkboxTargets.forEach((cb) => {
      cb.checked = true
      cb.disabled = true
    })
    if (this.hasSelectAllTarget) this.selectAllTarget.disabled = true
    this.updateUI()
  }

  clearMatching(event) {
    if (event) event.preventDefault()
    this.matchingMode = false
    this.checkboxTargets.forEach((cb) => {
      cb.disabled = false
      cb.checked = false
    })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.disabled = false
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.updateUI()
  }

  // Show the status selector only for assign/set_status; show the source-branch
  // selector only for copy_branch. L6: also disable the hidden fields' inputs so
  // their values are never submitted (disabled inputs are omitted from the
  // form) — not just hidden via CSS.
  syncOperationFields() {
    if (!this.hasBulkActionTarget) return
    const action = this.bulkActionTarget.value

    if (this.hasStatusFieldTarget) {
      const hideStatus =
        action === "unassign" ||
        action === "copy_branch" ||
        action === "set_promotion"
      // NOTE: toggle real display, not the `hidden` utility class. These spans
      // are `inline-flex`, and Tailwind's `inline-flex` overrides `hidden` (both
      // set `display`, later rule wins) — so `.hidden` alone left the field
      // visible. Setting style.display wins regardless. (round 8 fix)
      this.#toggleField(this.statusFieldTarget, hideStatus)
      this.#setFieldDisabled(this.statusFieldTarget, hideStatus)

      // For `assign` the status is only the default for newly-assigned
      // branches — existing per-branch statuses are kept. Make the label say
      // so, and surface the hint. For `set_status` the status applies to every
      // targeted row, so it stays the plain "Status" label with no hint.
      const isAssign = action === "assign"
      if (this.hasStatusLabelTarget) {
        this.statusLabelTarget.textContent = isAssign
          ? this.defaultStatusLabelValue
          : this.statusLabelValue
      }
      if (this.hasStatusHintTarget) {
        this.#toggleField(this.statusHintTarget, !isAssign)
      }

      // The empty "keep / inherit" option is the default for `assign` (submit
      // without touching it → blank status → existing rows keep their status).
      // For `set_status` a blank status is a hard error, so the inherit option
      // is disabled and the selector falls back to the first real status.
      if (this.hasStatusSelectTarget && this.hasStatusInheritOptionTarget) {
        // Enable/disable the "keep / inherit" (blank) option. Routed through
        // Tom Select when present so the dropdown reflects it — a native
        // `option.disabled` is invisible to Tom Select — with a native
        // fallback for the pre-mount connect pass.
        this.#setInheritEnabled(isAssign)

        // Reset to inherit ONLY when coming from `set_status` — a status picked
        // there must NOT carry over and silently become the assign default (that
        // would re-introduce the "overwrite" surprise). Coming from `unassign`/
        // `copy_branch` (which hide the status field) there's no such surprise,
        // so a pick the user made earlier in `assign` is preserved. Within
        // assign the user's own pick is always preserved (userPickedStatus).
        const enteringAssignFromSetStatus =
          isAssign && this.lastAction === "set_status"
        if (enteringAssignFromSetStatus) this.userPickedStatus = false

        if (isAssign) {
          if (!this.userPickedStatus) this.#setStatusValue("")
        } else if (this.#statusValue() === "") {
          const firstReal = this.#statusOptions().find((o) => o.value !== "")
          if (firstReal) this.#setStatusValue(firstReal.value)
        }
      }
    }
    const isCopy = action === "copy_branch"
    if (this.hasSourceFieldTarget) {
      this.#toggleField(this.sourceFieldTarget, !isCopy)
      this.#setFieldDisabled(this.sourceFieldTarget, !isCopy)
    }
    // CONEK-5419: promotion selector shown only for set_promotion; its input is
    // disabled otherwise so promotion_status is never submitted by other actions.
    const isSetPromotion = action === "set_promotion"
    if (this.hasPromotionFieldTarget) {
      this.#toggleField(this.promotionFieldTarget, !isSetPromotion)
      this.#setFieldDisabled(this.promotionFieldTarget, !isSetPromotion)
    }
    // For copy_branch the branch grid is the COPY TARGET, so relabel it.
    if (this.hasBranchesLabelTarget) {
      this.branchesLabelTarget.textContent = isCopy
        ? this.branchesLabelCopyValue
        : this.branchesLabelValue
    }
    // L7: record last action at the very end so an early-return added above
    // can't leave lastAction stale (it gates the enteringAssignFromSetStatus
    // reset above).
    this.lastAction = action
  }

  #setFieldDisabled(wrapper, disabled) {
    wrapper.querySelectorAll("input, select, textarea").forEach((el) => {
      el.disabled = disabled
      // A Ui::SelectComponent mounts a Tom Select instance that does NOT reflect
      // native `el.disabled` mutations — setting the underlying <select>'s
      // disabled flag leaves the visible control stuck in its mounted state
      // (the promotion/source selects rendered permanently greyed out). Route
      // enable/disable through the instance when present. (CONEK-5419)
      const ts = el.tomselect
      if (ts) {
        if (disabled) ts.disable()
        else ts.enable()
      }
    })
  }

  // --- Status <select> access layer -------------------------------------
  // branch_status is a Ui::SelectComponent (Tom Select). Tom Select does not
  // reflect native mutations (`select.value = …`, `option.disabled = …`), so
  // we route value/option changes through its instance when it exists. Before
  // it mounts (Stimulus connect order isn't guaranteed) we fall back to the
  // native <select>; Tom Select then adopts that native value/options on init.

  #statusTS() {
    return this.hasStatusSelectTarget ? this.statusSelectTarget.tomselect : null
  }

  // Full status option list captured once in DOM order, so #setInheritEnabled
  // can rebuild the dropdown (dropping/restoring "keep") without reordering it.
  #captureStatusOptions() {
    this._statusOptions = this.hasStatusSelectTarget
      ? [...this.statusSelectTarget.options].map((o) => ({
          value: o.value,
          text: o.text,
        }))
      : []
  }

  #statusOptions() {
    return this._statusOptions || []
  }

  #statusValue() {
    const ts = this.#statusTS()
    if (ts) return ts.getValue()
    return this.hasStatusSelectTarget ? this.statusSelectTarget.value : ""
  }

  #setStatusValue(value) {
    const ts = this.#statusTS()
    if (ts) {
      ts.setValue(value, true) // silent: don't re-fire statusChanged
    } else if (this.hasStatusSelectTarget) {
      this.statusSelectTarget.value = value
    }
  }

  #setInheritEnabled(enabled) {
    // Keep the native <option> in sync too (fallback + form correctness).
    if (this.hasStatusInheritOptionTarget) {
      this.statusInheritOptionTarget.disabled = !enabled
    }
    const ts = this.#statusTS()
    if (!ts) return

    const wanted = enabled
      ? this.#statusOptions()
      : this.#statusOptions().filter((o) => o.value !== "")
    const current = ts.getValue()

    // Rebuild in captured order (sortField is null → insertion order wins),
    // so "keep / inherit" stays first when re-added.
    Object.keys(ts.options).forEach((v) => ts.removeOption(v))
    wanted.forEach((o) => ts.addOption(o))
    ts.refreshOptions(false)
    if (wanted.some((o) => o.value === current)) ts.setValue(current, true)
  }

  // Show/hide a field by toggling inline display directly. The `hidden` utility
  // class can't be trusted here because these wrappers also carry `inline-flex`,
  // which overrides it. Empty string clears any inline display and restores the
  // stylesheet's value (these spans own no other inline display, so this is
  // intentional — don't swap it for removeProperty thinking it's "more correct").
  #toggleField(el, hidden) {
    el.style.display = hidden ? "none" : ""
  }

  updateUI() {
    const selected = this.checkboxTargets.filter((cb) => cb.checked)
    const pageCount = selected.length
    const showBar = this.matchingMode || pageCount > 0

    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.toggle("hidden", !showBar)
    }
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.matchingMode
        ? this.totalMatchingValue.toString()
        : pageCount.toString()
    }

    // Hidden flag the controller reads to switch to filter-scoped processing.
    if (this.hasSelectAllMatchingTarget) {
      this.selectAllMatchingTarget.value = this.matchingMode ? "true" : "false"
    }

    // Page selection IDs are only sent when NOT in matching mode (the server
    // re-derives ids from the filter in matching mode).
    if (this.hasIdsContainerTarget) {
      this.idsContainerTarget.innerHTML = ""
      if (!this.matchingMode) {
        selected.forEach((cb) => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = "item_ids[]"
          input.value = cb.value
          this.idsContainerTarget.appendChild(input)
        })
      }
    }

    // Notes: page-selection vs all-matching.
    if (this.hasPageSelectionNoteTarget) {
      this.pageSelectionNoteTarget.classList.toggle(
        "hidden",
        this.matchingMode || pageCount === 0,
      )
    }
    if (this.hasMatchingNoteTarget) {
      this.matchingNoteTarget.classList.toggle("hidden", !this.matchingMode)
    }
    // Offer "select all matching" only when the page is fully selected and
    // there's more beyond the visible page.
    if (this.hasSelectAllMatchingToggleTarget) {
      const pageFull =
        pageCount > 0 && pageCount === this.checkboxTargets.length
      const moreThanPage = this.totalMatchingValue > this.checkboxTargets.length
      this.selectAllMatchingToggleTarget.classList.toggle(
        "hidden",
        this.matchingMode || !(pageFull && moreThanPage),
      )
    }
  }

  allBranchesChecked() {
    return (
      this.branchCheckboxTargets.length > 0 &&
      this.branchCheckboxTargets.every((cb) => cb.checked)
    )
  }

  toggleAllBranches() {
    const check = !this.allBranchesChecked()
    this.branchCheckboxTargets.forEach((cb) => (cb.checked = check))
    this.updateBranchToggleLabel()
  }

  updateBranchToggleLabel() {
    if (!this.hasBranchSelectAllButtonTarget) return
    const allChecked = this.allBranchesChecked()
    this.branchSelectAllButtonTarget.textContent = allChecked
      ? this.unselectAllBranchesLabelValue
      : this.selectAllBranchesLabelValue
    // a11y: the changing label alone reads as two unrelated buttons to AT;
    // aria-pressed exposes the toggle state so it's announced on focus.
    this.branchSelectAllButtonTarget.setAttribute(
      "aria-pressed",
      String(allChecked),
    )
  }
}
